import cn from 'classnames'
import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './CockpitDrawer.module.scss'

/** The element a drawer opens over: the page area under the app's own bars, marked with
 *  `data-help-panel-host` by the page hosting the trigger, so the shade covers the partner column
 *  and the board but none of the app's chrome. */
export const COCKPIT_DRAWER_HOST_SELECTOR = '[data-help-panel-host]'

/** As long as the panel's way out: it stays mounted that long after closing. */
const CLOSE_DURATION_MS = 200

interface ICockpitDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** The page area the panel and its shade start at; without one they take the whole height. */
  getHost: () => HTMLElement | null | undefined
  /** The page edge the panel slides in from. */
  side?: 'left' | 'right'
  /** The heading drawn at the top of the panel, unless `renderHeader` draws a header of its own. */
  title: ReactNode
  /** Draws the panel's header in place of the title, e.g. a band about the partner the panel
   *  shows. It is handed the close button, to place in it. */
  renderHeader?: (closeButton: ReactNode) => ReactNode
  /** The panel's accessible name. */
  ariaLabel: string
  /** The close button's accessible name. */
  closeLabel: string
  /** Id of the panel, for the button that opens it to point at. */
  id?: string
  /** Extra class on the panel, e.g. to set `--cockpit-drawer-width` for wider content. */
  className?: string
  /** Where the panel is drawn instead of the page's body: a dialog's own element, for a panel opened
   *  from a control over that dialog to stand over it too, the whole height down to the bar. */
  container?: HTMLElement | null
  children: ReactNode
}

const CloseIcon = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <path d="M2 2l8 8M10 2 2 10" />
  </svg>
)

/** A panel sliding in from the right or the left edge over a light shade. The shade, the close
 *  button and Escape all close it. Shade and panel cover the page area only: from under the app
 *  header and the partner bar down to the plan sections bar when one is pinned to the bottom. */
export const CockpitDrawer = ({
  isOpen,
  onClose,
  getHost,
  side = 'right',
  title,
  renderHeader,
  ariaLabel,
  closeLabel,
  id,
  className,
  container,
  children,
}: ICockpitDrawerProps) => {
  const closeRef = useRef<HTMLButtonElement>(null)
  const getHostRef = useRef(getHost)
  const [isMounted, setIsMounted] = useState(isOpen)
  const [top, setTop] = useState(0)
  const hasCustomHeader = Boolean(renderHeader)

  getHostRef.current = getHost

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
      return
    }

    const timeout = window.setTimeout(() => setIsMounted(false), CLOSE_DURATION_MS)

    return () => window.clearTimeout(timeout)
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isMounted) {
      return
    }

    /* Placed once on opening and again on resize, never on scroll: a shade chasing the page lags
       it by a frame and shakes. It starts under the app header and the partner bar; without the
       bar, at the page area itself. */
    const place = () => {
      const headerBottom = document.querySelector('header')?.getBoundingClientRect().bottom ?? 0
      const partnerBar = document.querySelector('[data-partner-bar]')
      const areaTop = partnerBar
        ? partnerBar.getBoundingClientRect().bottom
        : (getHostRef.current()?.getBoundingClientRect().top ?? 0)

      setTop(Math.max(0, headerBottom, areaTop))
    }

    /* The page holds still while the panel is open, so the shade keeps to the partner bar. The
       scrollbar's room is kept, so the page does not shift sideways. */
    const rootStyle = document.documentElement.style
    const previousOverflow = rootStyle.overflow
    const previousPaddingRight = rootStyle.paddingRight
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

    rootStyle.overflow = 'hidden'

    if (scrollbarWidth > 0) {
      rootStyle.paddingRight = `${scrollbarWidth}px`
    }

    place()
    window.addEventListener('resize', place)

    return () => {
      window.removeEventListener('resize', place)
      rootStyle.overflow = previousOverflow
      rootStyle.paddingRight = previousPaddingRight
    }
  }, [isMounted])

  /* Focus starts on the close button, and goes along with it when a header of the consumer's own
     takes the title's place and draws the button anew. */
  useEffect(() => {
    if (isOpen && isMounted) {
      closeRef.current?.focus()
    }
  }, [hasCustomHeader, isMounted, isOpen])

  useEffect(() => {
    if (!isOpen || !isMounted) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMounted, isOpen, onClose])

  if (!isMounted) {
    return null
  }

  const closeButton = (
    <button
      ref={closeRef}
      type="button"
      className={cn(styles.close, { [styles.closeInHeader]: hasCustomHeader })}
      aria-label={closeLabel}
      onClick={onClose}
    >
      <CloseIcon />
    </button>
  )

  return createPortal(
    <div
      className={cn(styles.root, {
        [styles.left]: side === 'left',
        [styles.closing]: !isOpen,
        [styles.inDialog]: Boolean(container),
      })}
      style={{ top: container ? 0 : top }}
    >
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" />
      <div
        id={id}
        className={cn(styles.panel, className)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {renderHeader ? (
          <div className={styles.customHeader}>{renderHeader(closeButton)}</div>
        ) : (
          <div className={styles.header}>
            <span className={styles.title}>{title}</span>
            {closeButton}
          </div>
        )}
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    container ?? document.body
  )
}

CockpitDrawer.displayName = 'CockpitDrawer'
